/* Supabase data layer for the console's own tables.
 *
 * The board must keep working on a flaky salon wifi, so writes never block the
 * UI: every mutation is applied to local state optimistically and appended to a
 * durable queue here. The queue is replayed in order until it drains. Every op
 * is an upsert or a delete keyed by a client-generated uuid, so replaying one
 * twice is harmless.
 *
 * Knows nothing about the store — it moves rows, the store owns meaning. It also
 * knows nothing about any outside system: appointments, checkouts and the
 * service catalogue belong to whichever `scheduling` plugin is switched on, and
 * those read through `sb` and write through `enqueue` from their own file.
 */
import * as Vue from 'vue';
import { createClient } from '@supabase/supabase-js';
import { prefs } from './prefs.js';
import { tenant } from './tenant.js';
import { opKey, pendingKeysFrom, sameBatch, upsertOptions } from './rules/queue.js';
import { isLostTurnRace } from './rules/turn-id.js';
// Shared with store.js so a service read out of the database and the same
// service built from the Settings form draw the same letter on the square.
import { cleanShortcut, serviceAbbrev } from './rules/services.js';

var reactive = Vue.reactive;
var cfg = (window.DreamDash && window.DreamDash.config) || null;

/* js/config.js is not in the repo — it names one Supabase project, so it
 * belongs to the deployment. A checkout that has not been given one therefore
 * arrives here with nothing, and the failure is invisible: this file throws,
 * store.js and app.js throw after it, Vue never mounts and the station gets a
 * white page. Say what is wrong instead, outside #app so that Vue clearing its
 * own container on the way down does not take the message with it.
 */
function unconfigured() {
  var el = document.createElement('div');
  el.setAttribute('style', 'position:fixed;inset:0;z-index:9999;display:flex;' +
    'align-items:center;justify-content:center;padding:24px;background:#fff;' +
    'color:#1a1a1a;font:16px/1.7 system-ui,-apple-system,sans-serif');
  el.innerHTML =
    '<div style="max-width:44em">' +
    '<h1 style="font-size:1.4em;margin:0 0 .6em">Console is not configured</h1>' +
    '<p style="margin:0 0 1em"><code>js/config.js</code> is missing, or still ' +
    'holds the placeholder values it shipped with.</p>' +
    '<p style="margin:0">Copy <code>public/js/config.example.js</code> to ' +
    '<code>public/js/config.js</code> and fill in the Supabase URL and ' +
    'publishable key for this install. See ' +
    '<code>docs/configuration.md</code>.</p>' +
    '</div>';
  document.body.appendChild(el);
  // Every module downstream of this one fails once it throws, and their errors
  // land first-looking in the console. Name the real cause ahead of that
  // cascade so the console agrees with the page.
  console.error('Console is not configured: js/config.js is missing or ' +
    'still holds placeholder values. Copy dash/public/js/config.example.js to ' +
    'dash/public/js/config.js. The errors below this line are downstream of it.');
}

if (!cfg || !cfg.supabaseUrl || !cfg.supabaseKey ||
    cfg.supabaseUrl.indexOf('YOUR_') !== -1 || cfg.supabaseKey.indexOf('YOUR_') !== -1) {
  unconfigured();
  // This was `return` out of an IIFE. A module cannot return, and it should not
  // want to: carrying on would export a `sync` with no client behind it and the
  // failure would surface somewhere further away. Throwing stops every importer
  // dead, which is what used to happen anyway — the difference is that the
  // message above is already on screen by now.
  throw new Error('Console is not configured — see dash/public/js/config.example.js');
}

// Namespaced by tenant — see tenant.js. Read through tenant.ns() at every use
// rather than resolved once, because the tenant is not known until the session
// resolves and these are written before that.
var QUEUE_BASE = 'dreamspa.dash.queue.v1';
var CACHE_BASE = 'dreamspa.dash.cache.v1';
var RETRY_MS = 4000;

/* `worker`: the socket's heartbeat runs in a Web Worker. A browser throttles a
 * hidden or covered tab's timers to about once a minute, which is longer than
 * the server waits for a heartbeat — so the socket was closed and reopened
 * roughly every minute on the Mac front desk all day on 2026-10-31, and every
 * event in between was lost. The worker script is inline (a Blob built by
 * realtime-js), not fetched, so it works with the salon's internet down. Off
 * where there is no Worker, because realtime-js throws rather than falling back. */
var sb = createClient(cfg.supabaseUrl, cfg.supabaseKey, {
  auth: { persistSession: true, autoRefreshToken: true },
  realtime: {
    params: { eventsPerSecond: 5 },
    worker: typeof window !== 'undefined' && typeof window.Worker !== 'undefined'
  }
});

var status = reactive({
  online: true,
  pending: 0,
  lastError: null,
  loaded: false,
  /* Writes the server actively refused and the queue therefore threw away.
   *
   * Separate from lastError, and deliberately never cleared, because the two
   * mean opposite things about the future. lastError is "the last attempt did
   * not land", and a successful flush genuinely answers it. A drop is "this
   * change is gone and nothing will retry it" — a later flush succeeding says
   * nothing about the row that was discarded ten minutes ago, so letting the
   * green pill come back would be the console stating something false.
   *
   * It survives until the page is reloaded, which is also when the board next
   * reads the server and finds out what actually got saved. */
  dropped: 0,
  lastDrop: null
});

/* ---------------- auth ----------------
 * Every tt_* table is `authenticated`-only, so the board itself needs a
 * station sign-in — not just the appointments pane. Signed out, the console
 * shows the last cached snapshot and can save nothing.
 */
// `tenantId` starts from the cached value in tenant.js rather than null. A
// station that has been signed in before therefore boots knowing which salon it
// is, which is what lets the queue and the settings mirror be read from the
// right key before the network has answered anything.
var auth = reactive({
  user: null, checking: true, error: null, busy: false,
  tenantId: tenant.get()
});

/* Which salon this session is.
 *
 * Asked of the database rather than worked out here, and that is the point:
 * `current_tenant_id()` is a wrapper around the very function every RLS policy
 * runs, so the answer the board files its writes under and the answer the
 * database will accept them under cannot drift apart. It also takes no
 * arguments, so it can only ever be about the caller.
 *
 * This used to read tenant_members directly, which was the same answer for the
 * only kind of session that existed. It is no longer: a platform admin in a
 * support session has no membership row at all and is nonetheless, for the
 * length of that session, this salon — see
 * admin/server/schema/13-impersonation.sql. Reading the table would leave such a
 * session stamping its writes with the wrong tenant, or with none.
 *
 * The fallback is not defensive habit. This console has to run against a project
 * where the admin schema has never been applied and that function does not
 * exist, and on that project the old lookup is exactly right.
 *
 * A null answer — no membership, no session, or an expired one — returns without
 * adopting anything, which is the same "not attached to a salon yet" state this
 * has always had.
 *
 * When the answer differs from what this browser had cached, the queue, the
 * board cache and the settings mirror are all moved into the new tenant's
 * namespace. On the first sign-in after the cutover the previous namespace is
 * the un-namespaced one, so this is also the migration off the old fixed keys.
 */
async function resolveTenant() {
  try {
    var id = null;
    var rpc = await sb.rpc('current_tenant_id');
    if (rpc.error) {
      var res = await sb.from('tenant_members').select('tenant_id').limit(1).maybeSingle();
      if (res.error || !res.data) return;
      id = res.data.tenant_id;
    } else {
      id = rpc.data || null;
    }
    if (!id) return;
    var moved = tenant.adopt(id);
    auth.tenantId = tenant.get();
    if (moved) {
      tenant.move(QUEUE_BASE, moved.previous);
      tenant.move(CACHE_BASE, moved.previous);
      prefs.renamespace(moved.previous);
      // The queue in memory was read from the old key at load. Re-read it from
      // the new one so the two agree; anything enqueued in between is already
      // in `queue` and would be lost by a blind replace, so this merges.
      adoptQueue();
    }
  } catch (e) { /* offline — the cached tenant stands until the network returns */ }
}

/* ---------------- support sessions ----------------
 *
 * Whether this console is somebody's own salon or a platform admin's support
 * session — see admin/server/schema/13-impersonation.sql, and js/rules/support.js
 * for what the banner then says.
 *
 * `ended` is remembered rather than derived, because a session that has been
 * stopped by hand is indistinguishable from never having had one: the status
 * function returns nothing in both cases. Only this tab knows it was in one.
 */
var support = reactive({ tenantName: null, expiresAt: null, reason: null, ended: false });

/* Asked of the same function the admin app's own banner reads, so the two
 * cannot disagree about whether a session is live. It takes no arguments and is
 * granted to `authenticated`, so an ordinary station may call it and gets an
 * empty answer — which is why this runs for every session rather than only for
 * sessions somebody has already decided are an admin's.
 *
 * A missing function is the ordinary case, not an error: this console has to run
 * against a project where the admin schema was never applied. So an error of any
 * kind — that, or being offline — leaves what is on screen alone rather than
 * reporting "no session", which is the one wrong answer here.
 */
async function resolveSupport() {
  try {
    var res = await sb.rpc('admin_impersonation_status');
    if (res.error) return;
    var row = (res.data && res.data[0]) || null;
    if (row) {
      support.tenantName = row.tenant_name;
      support.expiresAt = row.expires_at;
      support.reason = row.reason;
      support.ended = false;
    } else if (support.tenantName) {
      support.ended = true;
    }
  } catch (e) { /* offline — the banner stands until the network returns */ }
}

/* Re-asked when the tab comes back to the front.
 *
 * The countdown expires on its own, but a session ENDED from the admin app has
 * no clock to run out: the row is simply gone, and the tab it was for would go
 * on showing a live banner until its original expiry. Coming back to the tab is
 * both the moment that matters and the moment somebody is looking.
 */
document.addEventListener('visibilitychange', function () {
  if (!document.hidden && auth.user && support.tenantName) resolveSupport();
});

// Do not call realtime.setAuth() with a literal token here: supabase-js keeps
// `realtime.accessToken` as a callback that reads the current session, so
// pinning a string would survive past its expiry and silently kill realtime.
sb.auth.getSession().then(function (res) {
  var session = res.data && res.data.session;
  auth.user = (session && session.user) || null;
  auth.checking = false;
  if (auth.user) { resolveTenant(); resolveSupport(); }
}).catch(function () { auth.checking = false; });

sb.auth.onAuthStateChange(function (_evt, session) {
  auth.user = (session && session.user) || null;
  // Signing out leaves `tenantId` alone — the station is still this salon's
  // station, it just has nobody at it. See the note on `adopt` in tenant.js.
  if (auth.user) { resolveTenant(); resolveSupport(); return; }
  /* The support banner does not survive a sign-out, and unlike the tenant that
   * is not a compromise. It says "the person at this console is standing in
   * another salon"; with nobody signed in there is no such person, and the row
   * — which is the admin's, not this browser's — is untouched either way. */
  support.tenantName = null;
  support.expiresAt = null;
  support.reason = null;
  support.ended = false;
});

async function signIn(email, password) {
  auth.busy = true;
  auth.error = null;
  try {
    var res = await sb.auth.signInWithPassword({ email: email, password: password });
    if (res.error) { auth.error = res.error.message; return false; }
    return true;
  } catch (err) {
    auth.error = String(err && err.message ? err.message : err);
    return false;
  } finally {
    auth.busy = false;
  }
}

async function signOut() {
  try { await sb.auth.signOut(); } catch (e) { /* noop */ }
  auth.user = null;
}

function uuid() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* ---------------- durable write queue ---------------- */

function readQueue() {
  try {
    var stored = window.localStorage.getItem(tenant.ns(QUEUE_BASE));
    return stored ? (JSON.parse(stored) || []) : [];
  } catch (e) { return []; }
}

var queue = readQueue();

function persistQueue() {
  status.pending = queue.length;
  try {
    window.localStorage.setItem(tenant.ns(QUEUE_BASE), JSON.stringify(queue));
  } catch (e) { /* full */ }
}
persistQueue();

/* Called when the session tells us the tenant changed under us and tenant.move
 * has carried the stored queue across. Whatever was queued in memory between
 * load and that moment is not in the moved copy, so this puts the stored ops
 * first — they are older — and keeps the in-memory ones behind them. Order
 * between writes to different rows is the one thing the queue must not lose. */
function adoptQueue() {
  var stored = readQueue();
  if (!stored.length) { persistQueue(); return; }
  var pending = queue;
  queue = stored.concat(pending);
  persistQueue();
  flush();
}

var flushing = false;
var retryTimer = null;
// How many ops at the head of the queue are on the wire right now. Anything
// inside that window has already been sent, so it must not be rewritten
// underneath the request — the reply would splice away a write that never
// went anywhere.
var inFlight = 0;

// opKey and pendingKeysFrom live in js/rules/queue.js — pure functions, so
// they can be tested without a Supabase config. The header there says why that
// mattered enough to move them.

// op: { table, type: 'upsert' | 'delete' | 'update', row, match, onConflict,
//       ignoreDuplicates, patch, isNull }   (patch and isNull are the conditional
//       update's — see runOps; ignoreDuplicates is upsertOptions' in rules/queue.js)
//
// Stop a turn and then correct its times and the queue held two writes of
// the same row, the first of which is already wrong. Only the last state of
// a row is worth sending, so a fresh upsert replaces a pending one in place
// — in place, not appended, because the order of writes to DIFFERENT rows
// still has to hold.
/* Stamp the row with the salon it belongs to.
 *
 * ONE edit covers all 21 write sites — 11 in store.js, 6 in mangomint.js, 2 in
 * prefs.js — and none of them needs to know tenants
 * exist. That is the whole reason the queue is a chokepoint.
 *
 * It must run BEFORE opKey(), and that ordering is load-bearing rather than
 * tidy: opKey reads the conflict columns off `op.row`, and every conflict
 * target now leads with tenant_id. Stamp afterwards and the key comes back null
 * for every upsert, which silently disables the replace-a-pending-write
 * behaviour that stops a corrected turn from being sent twice with the first,
 * wrong, version going up first.
 *
 * Signed out, `tenant.get()` is null and nothing is written — deliberately.
 * Leaving the property absent lets the column DEFAULT
 * (private.auth_tenant_id()) stamp the row when it eventually goes up, which is
 * right. Sending an explicit null would override the default and fail the
 * not-null constraint instead.
 *
 * Deletes are not stamped: they carry a `match`, not a row, and RLS already
 * confines a delete to the caller's own tenant. A tenant_id in the match would
 * be a second copy of a rule the database is enforcing anyway.
 */
function stampTenant(op) {
  if (op.type !== 'upsert' || !op.row) return;
  if (op.row.tenant_id != null) return;
  var id = tenant.get();
  if (id) op.row.tenant_id = id;
}

function enqueue(op) {
  stampTenant(op);
  var key = opKey(op);
  if (key) {
    // Skip the in-flight window: those are already sent. A newer write of one
    // of those rows goes on the end and costs an extra round trip, which is
    // the correct trade — the alternative loses it.
    for (var i = inFlight; i < queue.length; i++) {
      if (opKey(queue[i]) === key) { queue[i] = op; persistQueue(); flush(); return; }
    }
  }
  queue.push(op);
  persistQueue();
  flush();
}

// How many ops from the front of the queue can go up as one request: a run
// of upserts to the same table with the same conflict target. PostgREST
// takes an array, so marking eight technicians in is one round trip rather
// than eight.
var MAX_BATCH = 50;

function batchLength() {
  var head = queue[0];
  if (head.type !== 'upsert') return 1;
  var n = 1;
  while (n < queue.length && n < MAX_BATCH) {
    if (!sameBatch(head, queue[n])) break;
    n += 1;
  }
  return n;
}

function runOps(ops) {
  var head = ops[0];
  var q = sb.from(head.table);
  if (head.type === 'delete') {
    var del = q.delete();
    Object.keys(head.match || {}).forEach(function (k) { del = del.eq(k, head.match[k]); });
    // `matchIn` deletes a whole list in one statement — clearing a service
    // mapping was one round trip per row.
    Object.keys(head.matchIn || {}).forEach(function (k) { del = del.in(k, head.matchIn[k]); });
    return del;
  }
  /* A patch that only lands if the row is still in the state this device
   * thought it was. `isNull` names columns that must still be empty on the
   * SERVER: the checkout stopping a square goes up this way, so a square
   * another screen stopped by hand meanwhile keeps that time instead of being
   * overwritten from a stale copy. A row that no longer qualifies is simply
   * not touched — no error, and the next reload brings the other screen's
   * version down. */
  if (head.type === 'update') {
    var upd = q.update(head.patch);
    Object.keys(head.match || {}).forEach(function (k) { upd = upd.eq(k, head.match[k]); });
    (head.isNull || []).forEach(function (k) { upd = upd.is(k, null); });
    return upd;
  }
  var rows = ops.map(function (o) { return o.row; });
  return q.upsert(rows.length === 1 ? rows[0] : rows, upsertOptions(head));
}

/* Anything queued for a salon this session is not signed in to.
 *
 * The namespaced key makes this rare — another tenant's ops live under another
 * key entirely — but not impossible: a write made seconds before a sign-out,
 * with the sign-in that follows belonging to somebody else, lands in the new
 * namespace with the old tenant's stamp on it. Sending it would be refused by
 * the WITH CHECK anyway; dropping it here says so in the log instead of leaving
 * a permanently rejected op at the head of the queue.
 *
 * Only runs with a tenant known, and only drops ops that carry a DIFFERENT one.
 * An op with no stamp at all predates the session and is left for the column
 * default to attribute.
 */
function dropForeignOps() {
  var id = tenant.get();
  if (!id) return;
  var kept = queue.filter(function (op) {
    return !(op.type === 'upsert' && op.row &&
             op.row.tenant_id != null && op.row.tenant_id !== id);
  });
  if (kept.length === queue.length) return;
  console.warn('Dropped ' + (queue.length - kept.length) +
               ' queued write(s) belonging to another salon.');
  queue = kept;
  persistQueue();
}

// A square refused because another screen already had it up — see flush().
var raceLost = false;
var raceListeners = [];
function onTurnRaceLost(fn) { raceListeners.push(fn); }

async function flush() {
  if (flushing || !queue.length) return;
  flushing = true;
  try {
    dropForeignOps();
    while (queue.length) {
      var n = batchLength();
      var ops = queue.slice(0, n);
      var res;
      try {
        inFlight = n;
        res = await runOps(ops);
        // A rejected BATCH is retried one row at a time, so a single refused
        // row cannot take the innocent ones beside it down with it.
        if (res && res.error && res.error.code &&
            res.error.code !== 'PGRST301' && n > 1) {
          n = 1;
          inFlight = 1;
          ops = queue.slice(0, 1);
          res = await runOps(ops);
        }
      } finally {
        inFlight = 0;
      }
      if (res && res.error && isLostTurnRace(ops[0].table, res.error)) {
        /* Another screen signed in as this station put up the square for the
         * same booking first (js/rules/turn-id.js). Nothing is lost — the
         * server has the square — so it is not counted as a refused write;
         * the store reads the day's squares back so this screen's copy gives
         * way to the one that stands. */
        console.info('A square for this booking was already up on another ' +
                     'screen; using that one.', ops[0].row);
        queue.splice(0, n);
        persistQueue();
        raceLost = true;
        continue;
      }
      if (res && res.error) {
        // A rejected row (constraint violation) would block the queue forever.
        // Network-ish failures retry; anything the server actively refused is
        // dropped so one bad op cannot freeze every later write.
        //
        // Dropping is right, and it is also data loss, so it says so. It used
        // to say so only in lastError — a title attribute on the sync pill —
        // while the pill itself went back to a green "Synced" as soon as the
        // queue drained. A schema the console had outgrown by two columns then
        // refused every roster write for a whole morning without one thing on
        // screen looking wrong, and the day's staff were only discovered
        // missing at the next sign-in. Loud in the console, and sticky in the
        // pill, because nobody is hovering a tooltip to check.
        if (res.error.code && res.error.code !== 'PGRST301') {
          status.lastError = ops[0].table + ': ' + res.error.message;
          status.dropped += n;
          status.lastDrop = ops[0].table + ': ' + res.error.message;
          console.error('Dropped ' + n + ' refused write(s) to ' + ops[0].table +
                        ' — ' + res.error.message, ops.map(function (o) {
            return o.type === 'delete' ? o.match : o.row;
          }));
          queue.splice(0, n);
          persistQueue();
          continue;
        }
        throw new Error(res.error.message);
      }
      queue.splice(0, n);
      persistQueue();
    }
    status.online = true;
    status.lastError = null;
    if (raceLost) {
      raceLost = false;
      raceListeners.forEach(function (fn) { try { fn(); } catch (e) { /* its own */ } });
    }
  } catch (err) {
    status.online = false;
    status.lastError = String(err && err.message ? err.message : err);
    if (!retryTimer) {
      retryTimer = setTimeout(function () { retryTimer = null; flush(); }, RETRY_MS);
    }
  } finally {
    flushing = false;
  }
}

window.addEventListener('online', flush);

function hasPendingWrites() { return queue.length > 0; }

// Which rows of `table` still have a write waiting to go up. See
// js/rules/queue.js for what is in a key and, more to the point, what is not.
function pendingKeys(table) { return pendingKeysFrom(queue, table); }

/* ---------------- reads ---------------- */

function num(v) { return v == null ? 0 : Number(v); }
function ms(v) { return v ? new Date(v).getTime() : null; }

function mapService(r) {
  // Null in the column and '' in the store: the hotkey is optional, and the
  // board's `shortcutMap` wants something falsy for a service nobody presses
  // rather than the `?` this used to invent — which was a real key on a real
  // keyboard, quietly bound to whichever service happened to be missing one.
  var shortcut = cleanShortcut(r.shortcut);
  return {
    id: r.id,
    name: r.name,
    shortcut: shortcut,
    // Never empty: the single fallback point, so everything downstream can
    // draw `abbrev` without asking whether one was ever set — and with the key
    // now optional, the name is what a service with neither falls back to.
    abbrev: serviceAbbrev(r.abbrev, r.name, shortcut),
    turnWeight: num(r.turn_weight),
    // Whether it gets a button in the Quick add bar. Default on: a service
    // added before this column existed should still show up.
    quickAdd: r.quick_add !== false,
    /* And whether a client may pick it on the lobby screen — a separate
     * question, deliberately, rather than a second use of `quickAdd`.
     *
     * They come apart in both directions. A salon puts its rarely-booked
     * services out of the desk's way while still letting somebody check in for
     * one; another offers three simple things on the tablet and keeps the
     * ninety-minute sets a conversation at the desk. Sharing one flag would
     * make the second impossible without also taking those services off the
     * board's own bar.
     *
     * Default on, for the same reason as above: a service that predates the
     * column should not vanish from a screen when the column arrives. */
    selfCheckin: r.self_checkin !== false,
    /* What the booking system calls this service, for the one place the console
     * links OUT to it: the check-in screen's booking link, where it fills the
     * `{service}` placeholder so somebody who tapped Pedicure lands on Pedicure.
     * See js/rules/booking.js.
     *
     * A string rather than a number, and never null downstream, because it is
     * only ever substituted into a URL — Mangomint's ids are integers, another
     * system's are slugs, and nothing here needs to tell them apart. Empty is
     * "nobody has filled it in", which resolves to the booking site's own front
     * page rather than to an error. */
    bookingServiceId: String(r.booking_service_id == null ? '' : r.booking_service_id),
    // How long this type usually takes. Null is not "unknown" waiting to be
    // filled in — it is "do not put a countdown on it", which is how every
    // service behaved before the column existed.
    defaultMinutes: r.default_minutes == null ? null : Number(r.default_minutes),
    // Which side of the client it is — 'feet', 'hands' or null — so a pedicure
    // can be done alongside the same visit's manicure. js/rules/same-time.js.
    sameTime: r.same_time === 'feet' || r.same_time === 'hands' ? r.same_time : null,
    color: r.color || '#C5A059',
    sortOrder: r.sort_order || 0,
    active: r.active !== false
  };
}

function mapStaff(r) {
  return {
    id: r.id,
    name: r.name,
    serviceIds: r.service_ids || [],
    sortOrder: r.sort_order || 0,
    active: r.active !== false,
    // Only takes clients who ask for them, unless the day says otherwise —
    // js/rules/own-clients.js. Off for a row from before the column existed.
    ownClientsOnly: !!r.own_clients_only,
    // What the booking system calls them, as text. Read, never written from
    // here: the admin app and the daily catalogue sync own it. Empty until
    // somebody links them.
    externalStaffId: r.external_staff_id == null ? '' : String(r.external_staff_id)
  };
}

function mapTurn(r) {
  return {
    id: r.id,
    businessDate: r.business_date,
    staffId: r.staff_id,
    staffName: r.staff_name || '',
    serviceId: r.service_id,
    serviceName: r.service_name,
    serviceLetter: r.service_letter,
    turnWeight: num(r.turn_weight),
    requested: !!r.requested,
    // Held until Mangomint has the client checked in; see the store.
    waiting: !!r.waiting,
    startedAt: ms(r.started_at),
    endedAt: ms(r.ended_at),
    appointmentId: r.appointment_id || null,
    appointmentServiceId: r.appointment_service_id || null,
    // The booking's own ids: what the link is re-found by when the booking
    // system reissues its line ids, and who the client actually is.
    sourceServiceId: r.source_service_id == null ? null : Number(r.source_service_id),
    clientId: r.client_id == null ? null : Number(r.client_id),
    clientName: r.client_name || '',
    sourceServiceName: r.source_service_name || '',
    // The options the booking carried, as [{ id, name }]. On the turn rather
    // than read off the appointment so a finished square — and History, long
    // after the book has moved on — still knows what the work included.
    options: Array.isArray(r.options) ? r.options : [],
    /* Other booked services folded into this same square, as
     * [{ appointmentId, appointmentServiceId, sourceServiceId, clientId,
     *    clientName, sourceServiceName, serviceId, serviceName, serviceLetter,
     *    turnWeight, startedAt, endedAt, options }].
     *
     * A visit is not always one square's worth of work in one square's worth of
     * time: a manicure and a pedicure done in one sitting are two lines in the
     * book, two services the technician performed, and one turn. The square's
     * own columns carry the first of them and this carries the rest, each with
     * the times and the weight it had on its own, because History is asked what
     * was done and not only what it counted for.
     *
     * Snapshotted rather than read through to the book, for the same reason
     * `options` is: a finished square outlives the booking behind it. */
    extras: Array.isArray(r.extras) ? r.extras : [],
    // What the booking said this service was worth in minutes, and when it had
    // it ending. Null on anything not linked to a booking.
    scheduledMinutes: r.scheduled_minutes == null ? null : Number(r.scheduled_minutes),
    scheduledEndAt: ms(r.scheduled_end_at),
    /* Where this square's clock was told to start, on the rare one somebody was
     * asked about, and what the book said the booking started at when they
     * answered. Null on both for every square nobody was asked about.
     *
     * Read back rather than left behind, which is the whole point of it being a
     * column: the follow-up it drives — a re-timed appointment moving the
     * square's start — has to survive a reload and has to reach the station
     * that did not ask the question. See `reconcileTurns`.
     */
    startChoice: r.start_choice || null,
    startBookedAt: ms(r.start_booked_at),
    // Row position, kept separate from started_at so a running turn can be
    // moved to the bottom of another column without disturbing its timer.
    sortAt: ms(r.sort_at) || ms(r.started_at)
  };
}

/* The console's memory of today's book, keyed by booked service line.
 *
 * Keyed rather than listed because every question asked of it is about one
 * line — has this one moved, was this one here before — and the appointment
 * level questions are answered by walking the lines of that appointment. See
 * `bookSeenOf` and `apptSeen` in store.js.
 */
function mapBookSeen(rows) {
  var out = {};
  (rows || []).forEach(function (r) {
    out[r.appointment_service_id] = {
      appointmentId: r.appointment_id,
      seenStartAt: ms(r.seen_start_at),
      inService: !!r.in_service,
      lateArrival: !!r.late_arrival,
      writtenUpAt: ms(r.written_up_at)
    };
  });
  return out;
}

/* A self check-in — see js/rules/waitlist.js for what the console does with
 * one. `serviceId` can come back null where the row cannot: a service deleted
 * after somebody checked in for it sets the reference null and leaves the
 * snapshotted name, which is what keeps the entry readable rather than blank. */
function mapCheckin(r) {
  return {
    id: r.id,
    businessDate: r.business_date,
    clientName: r.client_name || '',
    serviceId: r.service_id || null,
    serviceName: r.service_name || '',
    // Anything this build does not recognise is treated as dealt with rather
    // than left in the desk's queue: a newer station's fourth status must not
    // put a row on the list that nothing here knows how to take off it.
    status: r.status === 'waiting' || r.status === 'seated' || r.status === 'left'
      ? r.status : 'left',
    createdAt: ms(r.created_at),
    resolvedAt: ms(r.resolved_at),
    turnId: r.turn_id || null
  };
}

/* The day itself — see tt_days in server/schema/02-console-tables.sql.
 *
 * Null rather than a default row when the date has none, and the difference
 * carries the meaning the board acts on: no row is "nobody has opened today",
 * which is the one state that makes the console ask who is in. A zeroed stand-in
 * would answer that question wrongly every morning. */
function mapDay(r) {
  if (!r) return null;
  return {
    businessDate: r.business_date,
    startedAt: ms(r.started_at),
    closedAt: ms(r.closed_at)
  };
}

function mapDayStaff(rows) {
  var dayStaff = {};
  (rows || []).forEach(function (r) {
    dayStaff[r.staff_id] = {
      present: r.present !== false,
      presentSince: ms(r.present_since) || Date.now(),
      missedTurns: r.missed_turns || 0,
      skipped: !!r.skipped,
      skipReason: r.skip_reason || '',
      skips: r.skips || [],
      // Why each of those was charged, in the same order (schema part 21);
      // shorter for squares charged before reasons were kept.
      skipWhy: Array.isArray(r.skip_why) ? r.skip_why : [],
      // Their own hours for the day, and null for either end nobody has set —
      // which is not "no hours", it is "whatever the board works out". See
      // `shiftOf` in store.js.
      shiftStartAt: ms(r.shift_start_at),
      shiftEndAt: ms(r.shift_end_at),
      // The shift end the end-of-day sweep has already acted on, and whether
      // the desk has waved away the "booked but not on the board" notice for
      // them. Both were per-tab and are now facts about the day — see
      // `sweepDoneForDay` and `dismissMissingBooked` in store.js.
      doneSweptAt: ms(r.done_swept_at),
      missingDismissed: !!r.missing_dismissed,
      // The desk's word on own-clients-only for today; null is "as usual".
      ownClientsOnly: r.own_clients_only == null ? null : !!r.own_clients_only,
      // What the board has done with Mangomint's day for them (schema part
      // 17): when the desk last typed Hours today, the start the not-in count
      // was worked out for, and the holds the desk brought them back from.
      workHours: workHoursFrom(r.work_hours),
      // When they were out of the rotation, not how many squares went past —
      // that is `skips` above. Carried as milliseconds like every other clock
      // in the store, and `to` stays null while a bypass is still open.
      skipSpans: (Array.isArray(r.skip_spans) ? r.skip_spans : []).map(function (s) {
        return { from: ms(s.from), to: s.to == null ? null : ms(s.to) };
      }).filter(function (s) { return s.from != null; })
    };
  });
  return dayStaff;
}

function workHoursFrom(v) {
  var w = v && typeof v === 'object' ? v : {};
  return {
    typedAt: typeof w.typedAt === 'number' ? w.typedAt : null,
    missedAt: typeof w.missedAt === 'number' ? w.missedAt : null,
    released: Array.isArray(w.released)
      ? w.released.filter(function (k) { return typeof k === 'string'; }) : [],
    // How the missed count was worked out — a record, read and kept as written.
    missedWhy: w.missedWhy && typeof w.missedWhy === 'object' ? w.missedWhy : null
  };
}

function ok(res) {
  if (res.error) throw new Error(res.error.message);
  return res.data || [];
}

/* One fetcher per part of the day, so a change to one table can be answered
 * by reloading that table alone. A realtime event used to pull all four back
 * — including the roster and the service list, which a turn cannot have
 * changed — and then replace every array wholesale, invalidating every
 * computed and forcing a full redraw of state the board already had right. */
var PARTS = {
  services: function () {
    return sb.from('tt_services').select('*').order('sort_order')
      .then(function (r) { return ok(r).map(mapService); });
  },
  staff: function () {
    return sb.from('tt_staff').select('*').order('sort_order')
      .then(function (r) { return ok(r).map(mapStaff); });
  },
  /* At most one row, and usually none until somebody opens up — so `maybeSingle`
   * rather than `single`, which treats "no row" as an error and would turn every
   * morning's first load into a failure that falls back to the cache. */
  day: function (businessDate) {
    return sb.from('tt_days').select('*').eq('business_date', businessDate)
      .maybeSingle()
      .then(function (r) {
        if (r.error) throw new Error(r.error.message);
        return mapDay(r.data);
      });
  },
  dayStaff: function (businessDate) {
    return sb.from('tt_day_staff').select('*').eq('business_date', businessDate)
      .then(function (r) { return mapDayStaff(ok(r)); });
  },
  turns: function (businessDate) {
    return sb.from('tt_turns').select('*').eq('business_date', businessDate)
      .order('started_at')
      .then(function (r) { return ok(r).map(mapTurn); });
  },
  /* A part of the day like the turns are, rather than a fetch of its own the
   * way the missed walk-ins are. The difference is what has to happen when one
   * changes: a missed walk-in is written once and read at the end of the day,
   * where a check-in has to be on the front desk's screen within a second of
   * the lobby tablet taking it. Being a part means one realtime event reloads
   * this table and nothing else, and means the queue is in the offline snapshot
   * — so a desk that loses its wifi keeps the list of who is standing there. */
  checkins: function (businessDate) {
    return sb.from('tt_checkins').select('*').eq('business_date', businessDate)
      .order('created_at')
      .then(function (r) { return ok(r).map(mapCheckin); });
  },
  /* What the board has already seen in today's book — see `noteBookSeen` in
   * store.js and the table's own note in the schema.
   *
   * A part rather than a fetch of its own because it has to be there before the
   * first book poll is compared against anything. Loaded out of order it would
   * not merely be late: the board would read an empty memory as "seeing today
   * for the first time" and go quiet for one pass, which is the reload bug this
   * table exists to end. It is in the offline snapshot for the same reason. */
  bookSeen: function (businessDate) {
    return sb.from('tt_book_seen').select('*').eq('business_date', businessDate)
      .then(function (r) { return mapBookSeen(ok(r)); });
  }
};

// `names` defaults to the whole day — the first load, and the fallback
// whenever something happened that we cannot attribute to one table.
async function fetchParts(businessDate, names) {
  var wanted = (names && names.length ? names : Object.keys(PARTS))
    .filter(function (n) { return PARTS[n]; });
  var data = await Promise.all(wanted.map(function (n) { return PARTS[n](businessDate); }));
  var out = {};
  wanted.forEach(function (n, i) { out[n] = data[i]; });
  return out;
}

function fetchDay(businessDate) { return fetchParts(businessDate, null); }

// Both of the floor columns stay null rather than falling back to a zero: a row
// written before the console recorded them has no answer, and "0 staff in, no
// wait" is a different and much louder claim than "nobody wrote it down".
function mapMissed(r) {
  return {
    id: r.id,
    businessDate: r.business_date,
    partySize: r.party_size || 1,
    serviceIds: r.service_ids || [],
    note: r.note || '',
    staffPresent: r.staff_present == null ? null : r.staff_present,
    staffAble: r.staff_able == null ? null : r.staff_able,
    // { serviceId: count } — null on rows written before schema part 25.
    staffAbleByService: r.staff_able_by_service || null,
    waitMinutes: r.wait_minutes == null ? null : r.wait_minutes,
    createdAt: ms(r.created_at)
  };
}

// Walk-ins turned away, for a single day or a History range.
async function fetchMissedWalkins(fromDate, toDate) {
  var res = await sb
    .from('tt_missed_walkins')
    .select('*')
    .gte('business_date', fromDate)
    .lte('business_date', toDate || fromDate)
    .order('created_at');
  if (res.error) throw new Error(res.error.message);
  return (res.data || []).map(mapMissed);
}

// Who was marked in on each day of a range. History reads this to say how big
// the floor was when a walk-in was turned away.
async function fetchDayStaffRange(fromDate, toDate) {
  var res = await sb
    .from('tt_day_staff')
    .select('business_date, staff_id, present, own_clients_only')
    .gte('business_date', fromDate)
    .lte('business_date', toDate || fromDate);
  if (res.error) throw new Error(res.error.message);
  return (res.data || []).map(function (r) {
    return {
      businessDate: r.business_date,
      staffId: r.staff_id,
      present: r.present !== false,
      // The desk's word for that day; null is "as usual" (rules/own-clients.js).
      ownClientsOnly: r.own_clients_only == null ? null : !!r.own_clients_only
    };
  });
}

/* The salon's settings. Read on its own rather than as part of fetchDay: the
 * day's tables are reloaded whenever a turn moves, and the settings have not
 * changed because somebody stopped a service. It answers with rows, not with
 * meaning — what the keys are is prefs' business. */
async function fetchSettings() {
  var res = await sb.from('tt_settings').select('key, value');
  if (res.error) throw new Error(res.error.message);
  return res.data || [];
}

/* Booked services the desk has marked as a client request, for one day. Rows
 * again, not meaning: which of them still counts is the store's business, and
 * it depends on who the book currently has on the booking. */
async function fetchApptRequests(businessDate) {
  var res = await sb.from('tt_appt_requests')
    .select('appointment_service_id, appointment_id, staff_id')
    .eq('business_date', businessDate);
  if (res.error) throw new Error(res.error.message);
  return (res.data || []).map(function (r) {
    return {
      appointmentServiceId: Number(r.appointment_service_id),
      appointmentId: r.appointment_id == null ? null : Number(r.appointment_id),
      staffId: r.staff_id || null
    };
  });
}

/* The salon's desk notes that are still open and have not ended, as the shape
 * js/rules/notes.js works in: every time in milliseconds. Rows again — which of
 * them to draw is the rules' business. The expiry cut is made here as well as
 * there, so a table of old notes is never what comes down the wire. */
async function fetchNotes() {
  var res = await sb.from('tt_notes')
    .select('id, body, author, source, color, urgent, expires_at, created_at, closed_at')
    .is('closed_at', null)
    .gt('expires_at', new Date().toISOString());
  if (res.error) throw new Error(res.error.message);
  return (res.data || []).map(function (r) {
    return {
      id: r.id,
      body: r.body,
      author: r.author || null,
      source: r.source,
      color: r.color,
      urgent: !!r.urgent,
      expiresAt: new Date(r.expires_at).getTime(),
      createdAt: new Date(r.created_at).getTime(),
      closedAt: r.closed_at ? new Date(r.closed_at).getTime() : null
    };
  });
}

// Per-day, per-tech totals for the History view.
async function fetchHistory(fromDate, toDate) {
  var res = await sb
    .from('tt_turns')
    .select('*')
    .gte('business_date', fromDate)
    .lte('business_date', toDate)
    .order('business_date', { ascending: false })
    .order('started_at');
  if (res.error) throw new Error(res.error.message);
  return (res.data || []).map(mapTurn);
}

/* ---------------- local cache (renders before the network answers) --------- */

function saveCache(payload) {
  try {
    window.localStorage.setItem(tenant.ns(CACHE_BASE), JSON.stringify(payload));
  } catch (e) { /* full */ }
}

function loadCache() {
  try {
    var raw = window.localStorage.getItem(tenant.ns(CACHE_BASE));
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

/* ---------------- realtime ---------------- */

/* Filtered to this salon, and deliberately NOT to the current business date.
 *
 * The tenant filter is safe where a date filter is not, and the difference is
 * REPLICA IDENTITY. Under the default, a DELETE carries only the row's primary
 * key, so a filter on any other column cannot see its value and drops the event
 * — removing a turn at one station would never reach the next one, which is a
 * silently wrong board. The tt_* tables are on REPLICA IDENTITY FULL precisely
 * so a tenant filter is correct on deletes as well as inserts; see
 * server/schema/02-console-tables.sql. business_date has no such backing, and
 * skipping the occasional refetch of a date nobody is looking at was never
 * worth it anyway — the saving that mattered was reloading one table instead of
 * four, in fetchParts.
 *
 * Without the filter, every salon's write wakes every other salon's board.
 * RLS already stops them reading the row, so this is bandwidth and wasted
 * renders rather than a leak — which is why an unknown tenant falls back to an
 * unfiltered subscription rather than to no subscription at all. That happens
 * only on a station's very first sign-in, before tenant.js has anything cached.
 */
/* `onJoined` is called every time the channel (re)joins. A rejoin means events
 * were missed and will not be replayed, so the caller reads again — see
 * js/rules/catch-up.js. */
function subscribe(onChange, onJoined) {
  var id = tenant.get();
  // One channel per tenant. Two salons sharing a channel name would share its
  // bindings, which is the same event storm by another route.
  var channel = sb.channel(id ? 'tt-board:' + id : 'tt-board');
  ['tt_turns', 'tt_day_staff', 'tt_days', 'tt_staff', 'tt_services',
   'tt_settings', 'tt_appt_requests', 'tt_checkins'].forEach(function (table) {
    var binding = { event: '*', schema: 'public', table: table };
    if (id) binding.filter = 'tenant_id=eq.' + id;
    channel.on('postgres_changes', binding, function () {
      onChange(table);
    });
  });
  channel.subscribe(function (state) {
    if (state === 'SUBSCRIBED' && onJoined) onJoined();
  });
  return channel;
}

/* Its own channel, unlike the tables above. Realtime refuses a whole channel over
 * one binding it cannot honour, and this table arrives with schema part 24: a
 * console that got there first would otherwise lose every live update on the board
 * for the sake of a quiet row. The row's own failure stays the row's. */
function subscribePendingMoves(onChange) {
  var id = tenant.get();
  var binding = { event: '*', schema: 'public', table: 'tt_pending_moves' };
  if (id) binding.filter = 'tenant_id=eq.' + id;
  var channel = sb.channel(id ? 'tt-pending:' + id : 'tt-pending');
  channel.on('postgres_changes', binding, function () { onChange(); });
  channel.subscribe();
  return channel;
}

/* The Optimizer's four tables, on their own channel for the same reason: they arrive
 * with schema part 24. Only asked for by a salon licensed for the module. */
function subscribeOptimizer(onChange) {
  var id = tenant.get();
  var channel = sb.channel(id ? 'tt-optimizer:' + id : 'tt-optimizer');
  ['tt_optimizer_settings', 'tt_optimizer_blocks', 'tt_optimizer_days', 'tt_optimizer_dismissals'].forEach(function (table) {
    var binding = { event: '*', schema: 'public', table: table };
    if (id) binding.filter = 'tenant_id=eq.' + id;
    channel.on('postgres_changes', binding, function () { onChange(); });
  });
  channel.subscribe();
  return channel;
}

/* Desk notes get a channel of their own. They are the newest table and the one
 * that most often arrives on a project that has not had its schema applied yet;
 * a channel that fails to join for that reason must not be the board's. Same
 * tenant filter and the same unfiltered fallback as subscribe(). `onJoined` runs
 * each time the channel reaches SUBSCRIBED, so the store can catch up on notes
 * posted while the socket was down. */
function subscribeNotes(onChange, onJoined) {
  var id = tenant.get();
  var channel = sb.channel(id ? 'tt-notes:' + id : 'tt-notes');
  var binding = { event: '*', schema: 'public', table: 'tt_notes' };
  if (id) binding.filter = 'tenant_id=eq.' + id;
  channel.on('postgres_changes', binding, function () { onChange(); });
  channel.subscribe(function (status) {
    if (status === 'SUBSCRIBED' && onJoined) onJoined();
  });
  return channel;
}

function removeChannel(channel) {
  try { sb.removeChannel(channel); } catch (e) { /* already gone */ }
}

export const sync = {
  removeChannel: removeChannel,
  sb: sb,
  status: status,
  auth: auth,
  support: support,
  signIn: signIn,
  signOut: signOut,
  uuid: uuid,
  onTurnRaceLost: onTurnRaceLost,
  enqueue: enqueue,
  flush: flush,
  hasPendingWrites: hasPendingWrites,
  pendingKeys: pendingKeys,
  fetchDay: fetchDay,
  fetchParts: fetchParts,
  fetchHistory: fetchHistory,
  fetchSettings: fetchSettings,
  fetchApptRequests: fetchApptRequests,
  fetchNotes: fetchNotes,
  fetchMissedWalkins: fetchMissedWalkins,
  fetchDayStaffRange: fetchDayStaffRange,
  saveCache: saveCache,
  loadCache: loadCache,
  subscribe: subscribe,
  subscribePendingMoves: subscribePendingMoves,
  subscribeOptimizer: subscribeOptimizer,
  subscribeNotes: subscribeNotes
};

// Settings read at page load, before this file ran, so anything the first-boot
// adoption pass queued has been waiting for a queue to exist. This hands prefs
// the queue as well as draining it — see the note on `attach` in prefs.js for
// why the dependency runs this way round rather than as an import.
prefs.attach(sync);
